/**
 * Harness upkeep for the content phase (decision 121: bots stay functional with every new system, measured only when
 * a step needs a number): what the action bots used in a duel or skirmish, per team. Stance (decision 65), reloads
 * and weapon choice (Phase 65), and formation slots (Phase 66). A functional check reads it: a system no bot ever
 * uses, or one that stalls them, shows here without a sweep.
 */
import type { GameState, GroundWeaponKind, Stance } from '../types';
import type { ActionBotStats } from './actionBot';

export interface TeamUsage {
  /** Share of the team's bot-ticks in each stance. */
  stance: Record<Stance, number>;
  /** Reloads started, per bot. */
  reloadsPerBot: number;
  /** 65.7: bandages started, per bot. */
  bandagesPerBot: number;
  /** Shots fired with each weapon. */
  /** Shots by weapon (S7: a rocket count only once a rocket is fired, so a match without them reports as before). */
  shots: ShotsByWeapon;
  /** Share of the team's bot-ticks spent keeping a formation slot (0 without formations). */
  formation: number;
  /** Gun decisions held because the shot would land within its own blast's reach of the gun (danger close), per team. */
  dangerCloseHolds: number;
}

export type ShotsByWeapon = { rifle: number; pistol: number } & Partial<Record<GroundWeaponKind, number>>;

/** Counts shots by the shooter's weapon as they are fired (call from the harness's `fired` events). */
export function shotTally() {
  const byTeam = new Map<number, ShotsByWeapon>();
  return {
    fired(state: GameState, unitId: number): void {
      const u = state.players.find(p => p.id === unitId);
      if (!u) return;
      const row = byTeam.get(u.teamIndex) ?? { rifle: 0, pistol: 0 };
      const w = u.weapon ?? 'rifle';
      row[w] = (row[w] ?? 0) + 1;
      byTeam.set(u.teamIndex, row);
    },
    shots: (team: number): ShotsByWeapon => byTeam.get(team) ?? { rifle: 0, pistol: 0 },
  };
}

/** Each team's usage from the bot drivers' stats and the shot tally. Teams with no bots are absent. */
export function usageByTeam(stats: Iterable<[number, ActionBotStats]>, state: GameState, tally: ReturnType<typeof shotTally>): Record<number, TeamUsage> {
  const acc = new Map<number, { stance: Record<Stance, number>; reloads: number; bandages: number; slot: number; danger: number; bots: number }>();
  for (const [id, st] of stats) {
    const team = state.players.find(p => p.id === id)?.teamIndex;
    if (team === undefined) continue;
    const a = acc.get(team) ?? { stance: { stand: 0, crouch: 0, prone: 0 }, reloads: 0, bandages: 0, slot: 0, danger: 0, bots: 0 };
    a.stance.stand += st.usage.stance.stand; a.stance.crouch += st.usage.stance.crouch; a.stance.prone += st.usage.stance.prone;
    a.reloads += st.usage.reloads; a.bandages += st.usage.bandages; a.slot += st.usage.slotTicks; a.danger += st.usage.dangerCloseHolds ?? 0; a.bots++;
    acc.set(team, a);
  }
  const out: Record<number, TeamUsage> = {};
  for (const [team, a] of [...acc].sort((x, y) => x[0] - y[0])) {
    const ticks = a.stance.stand + a.stance.crouch + a.stance.prone || 1;
    out[team] = {
      stance: { stand: a.stance.stand / ticks, crouch: a.stance.crouch / ticks, prone: a.stance.prone / ticks },
      reloadsPerBot: a.reloads / Math.max(1, a.bots),
      bandagesPerBot: a.bandages / Math.max(1, a.bots),
      shots: tally.shots(team),
      formation: a.slot / ticks,
      dangerCloseHolds: a.danger,
    };
  }
  return out;
}
